import { padStart } from "@fullcalendar/core/internal";

export function transformMongoAggregateToApexChart(
    data: any,
    type: any = "monthly",
    calculateCumulativeSum = true,
) {
    // Normalize and sort by the requested period
    const getSortKey = (id: string) => {
        if (type === "weekly") {
            // Expecting "W##-YYYY" format from backend
            if (/^W\d{1,2}-\d{4}$/.test(id)) {
                const [w, y] = id.split("-");
                const year = Number(y);
                const week = Number(w.replace("W", ""));
                return year * 100 + week;
            }
            // Fallback for other formats
            let year: number | null = null;
            let week: number | null = null;
            if (/^\d{4}-W\d{1,2}$/.test(id)) {
                const [y, w] = id.split("-W");
                year = Number(y);
                week = Number(w);
            } else if (/^\d{1,2}-\d{4}$/.test(id)) {
                const [w, y] = id.split("-");
                year = Number(y);
                week = Number(w);
            } else {
                const [m, y] = id.split("-").map(Number);
                year = y || 0;
                week = m || 0;
            }
            return (year as number) * 100 + (week as number);
        }
        if (type === "quarterly") {
            // Expecting "Q#-YYYY" format from backend
            const [q, y] = id.split("-");
            const quarter = Number(q.replace("Q", ""));
            const year = Number(y);
            return year * 10 + quarter;
        }
        if (type === "yearly") {
            const parts = id.split("-");
            const year = Number(parts.length === 2 ? parts[1] : parts[0]);
            return year;
        }
        // monthly (default), expecting "MM-YYYY" format from backend
        const [month, year] = id.split("-").map(Number);
        return year * 100 + month;
    };

    data.sort((a: any, b: any) => getSortKey(a._id) - getSortKey(b._id));

    const aggregatedData: any[] = [];
    let cumulativeSum = 0;
    let values: any[] = [];
    let currentPeriod = "";

    data.forEach((item: any) => {
        let periodKey = item._id; // Use the ID directly from backend
        
        if (currentPeriod !== periodKey) {
            currentPeriod = periodKey;
            cumulativeSum = 0;
            values = [];
        }

        // cumulative sum
        cumulativeSum += item.sum;
        values.push(item.sum);

        const existingEntry = aggregatedData.find((d) => d._id === periodKey);
        if (existingEntry) {
            existingEntry.sum = cumulativeSum;
            existingEntry.values = values;
            existingEntry.average = cumulativeSum / values.length;
        } else {
            aggregatedData.push({
                _id: periodKey,
                sum: cumulativeSum,
                values,
                average: cumulativeSum / values.length,
            });
        }
    });

    return {
        series: [
            {
                name: "Total",
                data: calculateCumulativeSum
                    ? aggregatedData.map((item: any) => item.sum)
                    : aggregatedData.map((item: any) => item.average),
            },
        ],
        options: {
            chart: {
                type: "bar",
                toolbar: {
                    tools: {
                        download: false,
                    },
                },
            },
            plotOptions: {
                bar: {
                    horizontal: false,
                    columnWidth: "25%",
                    borderRadius: 5,
                    borderRadiusApplication: "end",
                },
            },
            dataLabels: {
                enabled: false,
            },
            stroke: {
                show: true,
                width: 2,
                colors: ["transparent"],
            },
            xaxis: {
                categories: aggregatedData.map((item: any) => {
                    switch (type) {
                        case "weekly": {
                            // Expecting "W##-YYYY" format from backend
                            const [w, year] = item._id.split("-");
                            const weekNum = w.replace("W", "");
                            return `S${padStart(weekNum, 2)} ${year}`; // Semaine
                        }
                        case "monthly": {
                            // Expecting "MM-YYYY" format from backend
                            const [month, year] = item._id.split("-");
                            return `${padStart(month, 2)}/${year}`;
                        }
                        case "quarterly": {
                            // Expecting "Q#-YYYY" format from backend
                            const [q, year] = item._id.split("-");
                            return `${q} ${year}`;
                        }
                        case "yearly":
                            return item._id;
                        default:
                            return item._id;
                    }
                }),
            },
            yaxis: {
                labels: {
                    formatter: (val: any) => parseInt(val),
                },
            },
            tooltip: {
                custom: function({ series, seriesIndex, dataPointIndex, w }: any) {
                    const value = series[seriesIndex][dataPointIndex] || 0;
                    const period = w.globals.categoryLabels[dataPointIndex];
                    
                    return `
                        <div class="bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[150px]">
                            <div class="font-medium text-gray-900 mb-2">${period}</div>
                            <div class="flex justify-between items-center text-sm">
                                <span class="text-blue-600">● Total:</span>
                                <span class="font-medium">${value}</span>
                            </div>
                        </div>
                    `;
                }
            },
        },
    };
}

// Utilities to build period aggregates from raw events
type GetDate<T> = (item: T) => Date;
type GetValue<T> = (item: T) => number;

function pad2(n: number) {
    return String(n).padStart(2, "0");
}

function getISOWeekYear(date: Date) {
    // Convert to UTC to avoid TZ drifts
    const d = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
    );
    // Thursday in current week decides the year
    const day = d.getUTCDay() || 7; // 1..7 (Mon..Sun)
    d.setUTCDate(d.getUTCDate() + 4 - day); // move to Thursday
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const week = Math.ceil(((+d - +yearStart) / 86400000 + 1) / 7);
    return { week, year: d.getUTCFullYear() };
}

export function groupByPeriod<T>(
    items: T[],
    period: "weekly" | "monthly" | "quarterly",
    getDate: GetDate<T>,
    getValue: GetValue<T> = () => 1,
) {
    const map = new Map<string, number>();

    for (const item of items) {
        const dt = getDate(item);
        if (!(dt instanceof Date) || isNaN(+dt)) continue;
        let key: string;
        switch (period) {
            case "weekly": {
                const { week, year } = getISOWeekYear(dt);
                key = `W${pad2(week)}-${year}`;
                break;
            }
            case "monthly": {
                const y = dt.getFullYear();
                const m = dt.getMonth() + 1;
                key = `${pad2(m)}-${y}`;
                break;
            }
            case "quarterly": {
                const y = dt.getFullYear();
                const m = dt.getMonth() + 1;
                const q = Math.ceil(m / 3);
                key = `Q${q}-${y}`;
                break;
            }
        }
        map.set(key, (map.get(key) || 0) + getValue(item));
    }

    // Sort keys consistently using the same getSortKey logic as the transformer
    const keys = Array.from(map.keys()).sort((a, b) => {
        const sortKey = (id: string) => {
            if (period === "weekly") {
                const [w, y] = id.split("-");
                return Number(y) * 100 + Number(w.replace("W", ""));
            }
            if (period === "quarterly") {
                const [q, y] = id.split("-");
                return Number(y) * 10 + Number(q.replace("Q", ""));
            }
            // monthly
            const [m, y] = id.split("-");
            return Number(y) * 100 + Number(m);
        };
        return sortKey(a) - sortKey(b);
    });

    return keys.map((k) => ({ _id: k, sum: map.get(k) || 0 }));
}

// Helper to go from monthly aggregates to quarterly aggregates
export function monthlyAggregatesToQuarterly(
    monthly: { _id: string; sum: number }[],
) {
    const items = monthly.map((m) => {
        // Parse MM-YYYY into a Date (use first of month UTC)
        const [mm, yyyy] = m._id.split("-").map(Number);
        return {
            date: new Date(Date.UTC(yyyy, (mm || 1) - 1, 1)),
            value: m.sum,
        };
    });
    return groupByPeriod(
        items,
        "quarterly",
        (x) => x.date,
        (x) => x.value,
    );
}

// Transform two mongo aggregate results into a single Apex-compatible line chart
export function transformTwoMongoAggregatesToApexLineChart(
    a: { _id: string; sum: number }[],
    b: { _id: string; sum: number }[],
    type: any = "monthly",
    calculateCumulativeSum = true,
) {
    // Build maps
    const mapA = new Map<string, number>();
    const mapB = new Map<string, number>();

    for (const item of a || []) mapA.set(item._id, (mapA.get(item._id) || 0) + item.sum);
    for (const item of b || []) mapB.set(item._id, (mapB.get(item._id) || 0) + item.sum);

    // Union keys
    const keys = Array.from(new Set([...mapA.keys(), ...mapB.keys()]));

    // sort keys using same logic as above
    const getSortKey = (id: string) => {
        if (type === "weekly") {
            if (/^W\d{1,2}-\d{4}$/.test(id)) {
                const [w, y] = id.split("-");
                const year = Number(y);
                const week = Number(w.replace("W", ""));
                return year * 100 + week;
            }
            let year: number | null = null;
            let week: number | null = null;
            if (/^\d{4}-W\d{1,2}$/.test(id)) {
                const [y, w] = id.split("-W");
                year = Number(y);
                week = Number(w);
            } else if (/^\d{1,2}-\d{4}$/.test(id)) {
                const [w, y] = id.split("-");
                year = Number(y);
                week = Number(w);
            } else {
                const [m, y] = id.split("-").map(Number);
                year = y || 0;
                week = m || 0;
            }
            return (year as number) * 100 + (week as number);
        }
        if (type === "quarterly") {
            const [q, y] = id.split("-");
            const quarter = Number(q.replace("Q", ""));
            const year = Number(y);
            return year * 10 + quarter;
        }
        if (type === "yearly") {
            const parts = id.split("-");
            const year = Number(parts.length === 2 ? parts[1] : parts[0]);
            return year;
        }
        const [month, year] = id.split("-").map(Number);
        return year * 100 + month;
    };

    keys.sort((aKey, bKey) => getSortKey(aKey) - getSortKey(bKey));

    // Build arrays
    let valuesA: number[] = [];
    let valuesB: number[] = [];
    const categories: string[] = [];

    for (const k of keys) {
        categories.push(k);
        valuesA.push(mapA.get(k) || 0);
        valuesB.push(mapB.get(k) || 0);
    }

    if (calculateCumulativeSum) {
        for (let i = 1; i < valuesA.length; i++) valuesA[i] += valuesA[i - 1];
        for (let i = 1; i < valuesB.length; i++) valuesB[i] += valuesB[i - 1];
    }

    const formatCategory = (id: string) => {
        switch (type) {
            case "weekly": {
                const [w, year] = id.split("-");
                const weekNum = w.replace("W", "");
                return `S${padStart(weekNum, 2)} ${year}`;
            }
            case "monthly": {
                const [month, year] = id.split("-");
                return `${padStart(Number(month), 2)}/${year}`;
            }
            case "quarterly": {
                const [q, year] = id.split("-");
                return `${q} ${year}`;
            }
            case "yearly":
                return id;
            default:
                return id;
        }
    };

    return {
        series: [
            { name: "Contacted", data: valuesA },
            { name: "Responses", data: valuesB },
        ],
        options: {
            chart: {
                type: "area",
                toolbar: { show: false },
                sparkline: { enabled: false },
            },
            dataLabels: { enabled: false },
            stroke: { curve: "smooth", width: [2, 2] },
            markers: { size: 0 },
            fill: {
                type: "solid",
                opacity: 0.2,
                gradient: {
                    opacityTo: 0.08,
                    stops: [0, 90, 100],
                },
            },
            xaxis: {
                categories: categories.map(formatCategory),
                labels: { show: true, style: { colors: "#94a3b8", fontSize: "12px" } },
                axisBorder: { show: false },
                axisTicks: { show: false },
            },
            yaxis: {
                show: true,
                labels: { show: true, style: { colors: "#94a3b8", fontSize: "12px" } },
            },
            grid: { show: false },
            tooltip: { 
                shared: true, 
                intersect: false,
                custom: function({ series, seriesIndex, dataPointIndex, w }: any) {
                    const contacted = series[0][dataPointIndex] || 0;
                    const responded = series[1][dataPointIndex] || 0;
                    const replyRate = contacted > 0 ? ((responded / contacted) * 100).toFixed(1) : '0.0';
                    const period = w.globals.categoryLabels[dataPointIndex];
                    return `
                        <div class="bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[200px]">
                            <div class="text-gray-900 text-md underline font-bold mb-2">${period}</div>
                            <div class="space-y-1 text-sm">
                                <div class="flex justify-between items-center">
                                    <span class="text-black">Contacted:</span>
                                    <span class="font-medium">${contacted}</span>
                                </div>
                                <div class="flex justify-between items-center">
                                    <span class="text-black">Responded:</span>
                                    <span class="font-medium">${responded}</span>
                                </div>
                                <div class="flex justify-between items-center border-t pt-1 mt-2">
                                    <span class="text-black">Reply Rate:</span>
                                    <span class="font-medium text-blue-500">${replyRate}%</span>
                                </div>
                            </div>
                        </div>
                    `;
                }
            },
            legend: { show: false },
        },
    };
}
